import { useState } from 'react';
import { MoreVertical, Eye, Lock, Unlock, Trash2, ShieldCheck, LogOut, Key } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useGenerateResetLink } from '../hooks';
import { SuspendUserModal } from './SuspendUserModal';
import { RestoreUserModal } from './RestoreUserModal';
import { ForceLogoutModal } from './ForceLogoutModal';
import { RoleManagementModal } from './RoleManagementModal';
import { DeleteUserModal } from './DeleteUserModal';
import type { AdminUserRow } from '../types';

interface Props {
  user: AdminUserRow;
  canDelete?: boolean;
  canChangeRole?: boolean;
}

export function UserActionsDropdown({ user, canDelete, canChangeRole }: Props) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<null | 'suspend' | 'restore' | 'logout' | 'role' | 'delete'>(null);
  const navigate = useNavigate();
  const resetLink = useGenerateResetLink();
  const isSuspended = user.status === 'SUSPENDED';

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center"
        aria-label="User actions"
      >
        <MoreVertical className="w-4 h-4" />
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute right-0 mt-2 w-48 bg-surface-container-high border border-border-subtle rounded-lg shadow-xl z-30 overflow-hidden"
        >
          <Item icon={<Eye className="w-4 h-4" />} onClick={() => navigate(`/users/${user.id}`)}>
            View details
          </Item>
          {isSuspended ? (
            <Item icon={<Unlock className="w-4 h-4" />} onClick={() => { setOpen(false); setModal('restore'); }}>
              Restore
            </Item>
          ) : (
            <Item icon={<Lock className="w-4 h-4" />} onClick={() => { setOpen(false); setModal('suspend'); }}>
              Suspend
            </Item>
          )}
          <Item icon={<LogOut className="w-4 h-4" />} onClick={() => { setOpen(false); setModal('logout'); }}>
            Force logout
          </Item>
          <Item
            icon={<Key className="w-4 h-4" />}
            onClick={async () => {
              setOpen(false);
              try {
                const { link } = await resetLink.mutateAsync(user.id);
                await navigator.clipboard.writeText(link).catch(() => undefined);
                alert('Password reset link copied to clipboard');
              } catch (e) {
                alert((e as Error).message);
              }
            }}
          >
            Reset password
          </Item>
          {canChangeRole && (
            <Item icon={<ShieldCheck className="w-4 h-4" />} onClick={() => { setOpen(false); setModal('role'); }}>
              Change role
            </Item>
          )}
          {canDelete && (
            <Item icon={<Trash2 className="w-4 h-4" />} destructive onClick={() => { setOpen(false); setModal('delete'); }}>
              Delete
            </Item>
          )}
        </motion.div>
      )}

      <SuspendUserModal open={modal === 'suspend'} userId={user.id} userName={user.name} onClose={() => setModal(null)} />
      <RestoreUserModal open={modal === 'restore'} userId={user.id} userName={user.name} onClose={() => setModal(null)} />
      <ForceLogoutModal open={modal === 'logout'} userId={user.id} userName={user.name} onClose={() => setModal(null)} />
      <RoleManagementModal
        open={modal === 'role'}
        userId={user.id}
        currentRole={user.role}
        userName={user.name}
        onClose={() => setModal(null)}
      />
      <DeleteUserModal open={modal === 'delete'} userId={user.id} userName={user.name} onClose={() => setModal(null)} />
    </div>
  );
}

function Item({
  children,
  icon,
  onClick,
  destructive,
}: {
  children: React.ReactNode;
  icon: React.ReactNode;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'w-full px-3 py-2 text-left text-xs font-bold uppercase tracking-tight flex items-center gap-2 hover:bg-white/5 ' +
        (destructive ? 'text-red-400' : 'text-on-surface')
      }
    >
      {icon}
      {children}
    </button>
  );
}
